import {
  BO_KYC_PARENT_NODE_EXTRA_HEIGHT,
  BO_KYC_PARENT_NODE_EXTRA_WIDTH,
  BO_KYC_PARENT_NODE_HEADER_HEIGHT,
} from '../playbook-builder.constants';
import { reactFlowNodeType } from '../playbook-builder.types';
import type { ClientCenter, LayoutedNode } from './get-elements-with-layout';
import getElementsWithLayout from './get-elements-with-layout';
import getGraphDims from './get-graph-dims';
import type { InitialEdgeData, InitialNodeData } from './parse-graph';

interface BoKycNodesAndEdgesResult {
  nodes: LayoutedNode[];
  edges: InitialEdgeData[];
  clientCenter: ClientCenter;
}

/** Computes the size of the bo KYC graph and updates all nodes' sizes and positions  */
const getBoKycElementsWithLayout = (
  rootNodes: InitialNodeData[],
  boKycChildNodes: InitialNodeData[],
  rootEdges: InitialEdgeData[],
  boKycEdges: InitialEdgeData[],
): BoKycNodesAndEdgesResult => {
  // 1. Compute the size of the bo KYC subgraph
  const { nodes: layoutedBoKycChildNodes, edges: layoutedBoKycChildEdges } = getElementsWithLayout(
    boKycChildNodes,
    boKycEdges,
  );
  const boKycNodeGraphDims = getGraphDims({ nodes: layoutedBoKycChildNodes });

  // 2. Replace the bo_kyc node's width and height using the measure bo_kyc subgraph
  const boKycNodeId = rootNodes.find(node => node.type === reactFlowNodeType('bo_kyc'))?.id;
  const updatedRootNodes = rootNodes.map(node => {
    if (node.id !== boKycNodeId) {
      return node;
    }
    const dimensions = {
      width: boKycNodeGraphDims.width + BO_KYC_PARENT_NODE_EXTRA_WIDTH,
      height: boKycNodeGraphDims.height + BO_KYC_PARENT_NODE_EXTRA_HEIGHT,
    };
    return {
      ...node,
      ...dimensions,
      data: {
        ...node.data,
        // For some reason this must also be set on the data here???? Look into this
        ...dimensions,
      },
    };
  });

  // 3. Layout the root graph using the dimensions of the new bo_kyc
  const {
    nodes: layoutedRootNodes,
    edges: layoutedRootEdges,
    clientCenter,
  } = getElementsWithLayout(updatedRootNodes, rootEdges);

  // 4. Adjust the positions of all of the bo KYC nodes to be nested inside of the position of the bo KYC node computed above
  const boKycNode = layoutedRootNodes.find(node => node.id === boKycNodeId);
  if (!boKycNode) {
    throw new Error('bo_kyc node not found after layouting');
  }

  // we previously had the faulty assumption that the whole child graph would be centered
  // if the child graph is somewhat lopsided, this will cause the child graph to outside of the bo_kyc node bounding box
  // this offset moves the child graph to be totally contained by the bo_kyc node
  const parentCenterX = boKycNode.position.x + boKycNode.width / 2;
  const childGraphCenterX = boKycNodeGraphDims.originX + boKycNodeGraphDims.width / 2;
  const xOffset = parentCenterX - childGraphCenterX;

  const adjustedBoKycChildNodes = layoutedBoKycChildNodes.map(n => {
    // Translate all child nodes by the calculated x offset to center the child graph within the parent
    // and adjust y position to account for parent node header and extra height
    const position = {
      x: n.position.x + xOffset,
      y: n.position.y + boKycNode.position.y + BO_KYC_PARENT_NODE_HEADER_HEIGHT + BO_KYC_PARENT_NODE_EXTRA_HEIGHT / 2,
    };
    return { ...n, position };
  });

  // 5. Combine the root and boKyc graphs into one
  return {
    nodes: [...layoutedRootNodes, ...adjustedBoKycChildNodes],
    edges: [...layoutedRootEdges, ...layoutedBoKycChildEdges],
    clientCenter,
  };
};

export default getBoKycElementsWithLayout;
